// StatsRow — four-card schedule summary strip, identical on both
// views. Reuses the MetricCard scaffold so the strip cannot visually
// diverge from single-tab summaries. The zero-count tone rule (brand
// BAP-3) governs every problem card: alarm tone only when count > 0,
// neutral slate when 0. "In view" is a context number — always neutral.
//
// Counting:
//   Overdue          = status === 'red'                      (any kind)
//   Due this week    = status === 'brand'   AND date within 7 days of today
//   Pending decisions= status === 'amber'   AND kind ∈ {variation, deliverable}
//   In view          = totalInRange (server's count for the window)
//
// Each tile also carries a subtitle that explains the count — the
// wireframe shows a context sub-line beneath every value so the user
// reads the WHY at a glance ("across 3 contracts · worst 17d late",
// "2 insurances · 3 meetings · 2 deliverables", "May 2026 · 3
// contracts shown"). The subtitle is built from the same activity
// list the tile counts — never from a separate source — so the value
// and its explanation can never disagree.
//
// Click-to-filter: when onCardClick is supplied, cards 1-3 become
// interactive (MetricCard renders them as <button> with aria-pressed
// driven by activeKey===thisKey). Card 4 (In view) stays display-only
// — it is informational total context, not a filter that adds
// constraint. Omitting onCardClick keeps cards 1-3 non-interactive
// (back-compat with display-only consumers).

import MetricCard from "../tracking/deliverable/MetricCard";
import type { Activity, ActivityKind } from "../../../types/schedule";
import { matchesBucket } from "./scheduleBuckets";

export type StatsCardKey = "overdue" | "dueThisWeek" | "pendingDecisions";

interface StatsRowProps {
  activities: Activity[];
  totalInRange: number;
  today?: Date;
  /** Human label for the "In view" subtitle (e.g. "May 2026"). When
   *  omitted, the in-view subtitle still renders the distinct contract
   *  count but skips the period prefix. */
  periodLabel?: string;
  onCardClick?: (key: StatsCardKey) => void;
  activeKey?: StatsCardKey | null;
}

const KIND_LABEL_PLURAL: Record<ActivityKind, string> = {
  insurance: "insurances",
  deliverable: "deliverables",
  milestone: "milestones",
  meeting: "meetings",
  kpi: "KPIs",
  variation: "variations",
};

function distinctContractCount(activities: Activity[]): number {
  const set = new Set<string>();
  for (const a of activities) set.add(a.contract.id);
  return set.size;
}

function daysBetween(today: Date, iso: string): number | null {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  const todayMs = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
  const dm = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  return Math.round((todayMs - dm) / 86_400_000);
}

// Build "N <kind> · M <kind>" — only include kinds whose count > 0,
// preserving the wireframe's per-kind reading order.
function kindBreakdown(
  activities: Activity[],
  order: ActivityKind[],
): string {
  const counts = new Map<ActivityKind, number>();
  for (const a of activities) counts.set(a.kind, (counts.get(a.kind) ?? 0) + 1);
  const parts: string[] = [];
  for (const k of order) {
    const n = counts.get(k) ?? 0;
    if (n > 0) parts.push(`${n} ${KIND_LABEL_PLURAL[k]}`);
  }
  return parts.join(" · ");
}

export default function StatsRow({
  activities,
  totalInRange,
  today = new Date(),
  periodLabel,
  onCardClick,
  activeKey = null,
}: StatsRowProps) {
  const overdueActivities = activities.filter((a) =>
    matchesBucket(a, "overdue", today),
  );
  const dueSoonActivities = activities.filter((a) =>
    matchesBucket(a, "due-soon", today),
  );
  const pendingActivities = activities.filter((a) =>
    matchesBucket(a, "pending-decisions", today),
  );

  const overdueCount = overdueActivities.length;
  const dueThisWeekCount = dueSoonActivities.length;
  const pendingCount = pendingActivities.length;

  // Overdue subtitle: "across N contracts · worst Xd late".
  const overdueSubtitle = (() => {
    if (overdueCount === 0) return undefined;
    const contracts = distinctContractCount(overdueActivities);
    let worstDays = 0;
    for (const a of overdueActivities) {
      const d = daysBetween(today, a.date);
      if (d !== null && d > worstDays) worstDays = d;
    }
    const parts: string[] = [];
    parts.push(
      `across ${contracts} ${contracts === 1 ? "contract" : "contracts"}`,
    );
    if (worstDays > 0) parts.push(`worst ${worstDays}d late`);
    return parts.join(" · ");
  })();

  // Due-this-week subtitle: kind breakdown across all schedulable kinds.
  const dueSoonSubtitle =
    dueThisWeekCount === 0
      ? undefined
      : kindBreakdown(dueSoonActivities, [
          "insurance",
          "meeting",
          "deliverable",
          "milestone",
          "kpi",
          "variation",
        ]);

  // Pending-decisions subtitle: variation + deliverable kinds only.
  const pendingSubtitle =
    pendingCount === 0
      ? undefined
      : kindBreakdown(pendingActivities, ["variation", "deliverable"]);

  // In view subtitle: "{periodLabel} · N contracts shown".
  const inViewSubtitle = (() => {
    const contracts = distinctContractCount(activities);
    const contractsPart = `${contracts} ${contracts === 1 ? "contract" : "contracts"} shown`;
    return periodLabel ? `${periodLabel} · ${contractsPart}` : contractsPart;
  })();

  // Conditional onClick — back-compat: omitting onCardClick keeps the
  // cards display-only. Passing it makes the card a <button> via
  // MetricCard's existing onClick contract.
  const onClickFor = (key: StatsCardKey): (() => void) | undefined =>
    onCardClick ? () => onCardClick(key) : undefined;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        testId="schedule-stat-overdue"
        label="Overdue"
        value={overdueCount}
        valueTone={overdueCount > 0 ? "danger" : "neutral"}
        subtitle={overdueSubtitle}
        onClick={onClickFor("overdue")}
        isActive={activeKey === "overdue"}
      />
      <MetricCard
        testId="schedule-stat-due-week"
        label="Due this week"
        value={dueThisWeekCount}
        valueTone={dueThisWeekCount > 0 ? "brand" : "neutral"}
        subtitle={dueSoonSubtitle}
        onClick={onClickFor("dueThisWeek")}
        isActive={activeKey === "dueThisWeek"}
      />
      <MetricCard
        testId="schedule-stat-pending"
        label="Pending decisions"
        value={pendingCount}
        valueTone={pendingCount > 0 ? "warning" : "neutral"}
        subtitle={pendingSubtitle}
        onClick={onClickFor("pendingDecisions")}
        isActive={activeKey === "pendingDecisions"}
      />
      <MetricCard
        testId="schedule-stat-in-view"
        label="In view"
        value={totalInRange}
        valueTone="neutral"
        subtitle={inViewSubtitle}
      />
    </div>
  );
}
